import { z, toJSONSchema } from 'zod';
import Cookies from 'js-cookie';
import { TENANT_STORAGE_KEY } from '@constants/tenant';
import { SearchService } from '@services';
import { useServiceRef } from 'hooks/useServiceRef';
import { SortFieldsE, SortDirectionsE } from '@constants/catalog';
import { InputSchema, ToolDescriptor, ToolResponse } from '@mcp-b/webmcp-types';

const schema = z.object({
  query: z.string().describe('The query to search for products'),
  offset: z.number().describe('The offset to search for products').optional(),
  limit: z.number().describe('The limit to search for products').optional(),
  facetNames: z
    .array(z.string())
    .describe('The facet names to search for products')
    .optional(),
  selectedTerms: z
    .record(z.string(), z.array(z.string()))
    .describe('The selected terms to search for products')
    .optional(),
  sortBy: z
    .string()
    .regex(
      new RegExp(
        `^(${Object.values(SortFieldsE).join('|')}) (${Object.values(
          SortDirectionsE
        ).join('|')})$`
      )
    )
    .describe(
      'The sort by field and direction (e.g., "score desc", "price asc", "price desc")'
    )
    .optional()
});

export const searchProductsTool: ToolDescriptor<z.infer<typeof schema>, ToolResponse> = {
  name: 'search_products',
  description: `Searches for products matching the query. Schema: ${JSON.stringify(schema.shape)}`,
  inputSchema: toJSONSchema(schema) as InputSchema,
  execute: async ({
    query,
    offset = 0,
    limit = 20,
    facetNames = [],
    selectedTerms = {},
    sortBy = 'score desc'
  }: z.infer<typeof schema>): Promise<ToolResponse> => {
    const searchServiceRef = useServiceRef(SearchService);

    const tenantKey = JSON.parse(Cookies.get(TENANT_STORAGE_KEY)|| null)?.key;
    const response = await searchServiceRef.current.getProductsFullTextSearch(
      query,
      offset,
      limit,
      facetNames,
      selectedTerms,
      sortBy,
      tenantKey,
      undefined
    );

    return {
      content: [ {
        type: 'text',
        text: `Found ${response.total || 0} results. ${JSON.stringify(response)}`,
      } ]
    };
  }
};
